import { FlexHandle } from "@circle-react-uikit/CircleAIAppShell/Layout/Resizable/FlexHandle";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useRailbar } from "@circle-react/hooks/chatsV2/useRailbar";
import { useDmsV3Enabled } from "@circle-react/hooks/useDmsV3Enabled";
import { useMasqueradeEnabledAndActive } from "@circle-react/hooks/useMasqueradeEnabledAndActive";
import { useLocalStorage } from "@circle-react/hooks/utils/useLocalStorage";
import { motion } from "framer-motion";
import { isEmpty } from "lodash";
import type { ReactNode } from "react";
import { useCallback, useState } from "react";

import { t } from "@/i18n-js/instance";
import { useTrialBanner } from "@/react/components/Layout/TrialBanner/useTrialBanner";
import { useIsMobileShellEnabled } from "@/react/components/Mobile/hooks/useIsMobileShellEnabled";
import { useSmOrMdOrLgScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import { useElectronAPI } from "@/react/hooks/utils/useElectronAPI";

import { DEFAULT_RAILBAR_WIDTH } from "./constants";
import { Content } from "./Content";
import { Header } from "./Header";
import { RailSkeleton } from "./RailSkeleton";
import type { RailLoaderVariant } from "./types";

export interface RailProps {
  children?: ReactNode;
  className?: string;
  isBelowHeader?: boolean;
  isFullWidth?: boolean;
  isLoading?: boolean;
  isSmallScreen?: boolean;
  loaderVariant?: RailLoaderVariant;
  noBorderLeft?: boolean;
  shouldHideRailbarDragger?: boolean;
}

const RAILBAR_WIDTH_LOCAL_STORAGE_KEY = "circle-railbar-width";
const RAILBAR_MIN_WIDTH = 352;
const RAILBAR_MAX_WIDTH = 720;

const Rail = ({
  isBelowHeader = false,
  noBorderLeft = false,
  isLoading,
  loaderVariant = "rail-bar",
  children,
  className = "",
  shouldHideRailbarDragger = false,
  isFullWidth = false,
  isSmallScreen = false,
}: RailProps) => {
  const { isTrialBannerExists } = useTrialBanner();
  const isMobileShellEnabled = useIsMobileShellEnabled();
  const { isMessagingModalOpen, isMessagingPageOverlayOpen } =
    useDmsV3Enabled();
  const { isLiveStream, isCourseSpace, isDrawer, isChatSpace, isDmsPage } =
    useRailbar();
  const isSmOrMdScreen = useSmOrMdOrLgScreenMediaQuery() || isSmallScreen;
  const isMasqueradingEnabledAndActive = useMasqueradeEnabledAndActive();
  const { isElectron } = useElectronAPI();

  const { setValue, getValue } = useLocalStorage<number>(
    RAILBAR_WIDTH_LOCAL_STORAGE_KEY,
  );
  const [railbarWidth, setRailbarWidth] = useState<number>(
    getValue() || DEFAULT_RAILBAR_WIDTH,
  );

  const onResize = useCallback(
    (width: number) => {
      setRailbarWidth(width);
      setValue(width);
    },
    [setValue],
  );

  const isCourseMemberView = () => {
    const helloBar = document.getElementById("lesson-hello-bar");
    return isCourseSpace && isEmpty(helloBar);
  };

  const shouldHideRailbarDragOption =
    isLoading ||
    shouldHideRailbarDragger ||
    isLiveStream ||
    isCourseSpace ||
    isChatSpace;

  const railbarWidthStyle = shouldHideRailbarDragOption
    ? DEFAULT_RAILBAR_WIDTH
    : railbarWidth;
  return (
    <div
      className={classNames("flex h-full flex-row", className)}
      style={{ width: railbarWidthStyle }}
    >
      {!shouldHideRailbarDragOption && (
        <FlexHandle
          aria-label={t("circle_ai_shell.resize_panel")}
          width={railbarWidth}
          direction={-1}
          minWidth={RAILBAR_MIN_WIDTH}
          maxWidth={RAILBAR_MAX_WIDTH}
          onResize={onResize}
          className="z-[21] h-full invisible md:visible"
        />
      )}
      <motion.div
        id="rail-content"
        initial={isDmsPage ? { opacity: 0, x: 24 } : false}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className={classNames(
          "border-primary right-[inherit] top-0 z-20 flex h-full flex-col border-l opacity-100",
          {
            "bg-primary dark:bg-secondary dark:border-secondary":
              !isMobileShellEnabled,
            "bg-transparent": isMobileShellEnabled,
            fixed: !isMessagingPageOverlayOpen,
            "md:top-16": !isMessagingModalOpen,
            "lg:!top-0":
              isMessagingModalOpen ||
              isLiveStream ||
              isDrawer ||
              isCourseMemberView(),
            "lg:top-28":
              (isTrialBannerExists && !isCourseSpace) ||
              isMasqueradingEnabledAndActive,
            "lg:top-40":
              isTrialBannerExists &&
              !isCourseSpace &&
              isMasqueradingEnabledAndActive,
            "lg:top-20": isBelowHeader,
            "border-l-0": noBorderLeft,
            "z-50 !h-full !w-full": isSmOrMdScreen,
            "absolute flex !h-full flex-col overflow-hidden rounded-md":
              isLiveStream && !isSmOrMdScreen,
            "!w-[382px] rounded-xl": isLiveStream && !isFullWidth,
            "!w-full": isDrawer || isFullWidth,
            "!top-[6.325rem]": isElectron,
          },
        )}
        style={{ width: railbarWidthStyle }}
      >
        {isLoading ? <RailSkeleton variant={loaderVariant} /> : children}
      </motion.div>
    </div>
  );
};

Rail.Header = Header;
Rail.Content = Content;

export { Rail };
